import type { RefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Stack,
  Spacer,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
  Input as ChakraInput,
  HStack,
} from '@chakra-ui/react'

import type {
  InputFormProps as Props,
  NumberFilterValue,
} from '../../../../interfaces'
import { getFieldsMapping } from '../../../../../utils/mediaLibrary'
import OperatorMenu from '../../OperatorMenu'

const CustomMetadataNumberInputField = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    customMetadataField,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({
          fieldName: expression.fieldName,
          customMetadataField,
        }).getInitialValue(newOperator)
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: NumberFilterValue) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em" alignItems="stretch">
        <OperatorMenu
          width="full"
          expression={expression}
          customMetadataField={customMetadataField}
          handleOperatorChange={handleOperatorChange}
        />

        {[
          'IS',
          'IS_NOT',
          'STARTS_WITH',
          'GREATER_THAN',
          'LESS_THAN',
          'GREATER_THAN_EQUAL',
          'LESS_THAN_EQUAL',
        ].includes(expression.operator) && (
          <ChakraInput
            ref={initialFocusRef as RefObject<HTMLInputElement>}
            id={expression.fieldName}
            value={expression.value as string}
            onChange={e => {
              handleValueChange(e.target.value)
              setDirty(true)
            }}
            bgColor="white"
          />
        )}

        {['BETWEEN'].includes(expression.operator) && (
          <HStack spacing="0.5em" width="full">
            <ChakraInput
              ref={initialFocusRef as RefObject<HTMLInputElement>}
              placeholder="min."
              id={expression.fieldName}
              value={(expression.value as { min: string; max: string }).min}
              onChange={e => {
                const newValue = {
                  ...(expression.value as { min: string; max: string }),
                }
                newValue.min = e.target.value
                handleValueChange(newValue)
                setDirty(true)
              }}
              bgColor="white"
            />
            <ChakraInput
              placeholder="max."
              id={expression.fieldName}
              value={(expression.value as { min: string; max: string }).max}
              onChange={e => {
                const newValue = {
                  ...(expression.value as { min: string; max: string }),
                }
                newValue.max = e.target.value
                handleValueChange(newValue)
                setDirty(true)
              }}
              bgColor="white"
            />
          </HStack>
        )}

        <ChakraFormErrorMessage width="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default CustomMetadataNumberInputField
